import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getNews } from '../services/fortniteApi';
import { Newspaper, ExternalLink, Bookmark, Shield, Clock, Layout } from 'lucide-react';

const News = () => {
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('br'); // 'br', 'stw', or 'creative'

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const data = await getNews();
        setNews(data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
    </div>
  );

  const displayNews = news?.[activeTab]?.motds || [];

  const tabConfigs = {
    br: { label: 'Battle Royale', color: 'bg-blue-600', icon: Bookmark },
    stw: { label: 'Sauver le Monde', color: 'bg-orange-600', icon: Shield },
    creative: { label: 'Créatif', color: 'bg-purple-600', icon: Layout },
  };

  return (
    <div className="pt-[calc(var(--nav-height)+2rem)] pb-20 px-8 max-w-7xl mx-auto">
      <header className="mb-12 flex flex-col xl:flex-row xl:items-end justify-between gap-8">
        <div>
          <div className="flex items-center gap-2 mb-2 text-blue-500">
            <Newspaper className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-widest">Dernières Nouvelles</span>
          </div>
          <h1 className="text-5xl font-black uppercase mb-4">Mises à Jour Fortnite</h1>
          <p className="text-white/40 max-w-2xl">Restez informé des changements majeurs, des nouveaux skins et des événements en direct sur l'île.</p>
        </div>

        <div className="glass-card p-1.5 flex flex-wrap gap-1 self-start xl:self-end">
          {Object.entries(tabConfigs).map(([id, config]) => (
            (news?.[id] || id === 'br') && (
              <button 
                key={id}
                onClick={() => setActiveTab(id)}
                className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${activeTab === id ? `${config.color} text-white shadow-lg` : 'text-white/40 hover:text-white'}`}
              >
                <config.icon className="w-4 h-4" /> {config.label}
              </button>
            )
          ))}
        </div>
      </header>

      <AnimatePresence mode="wait">
        <motion.div 
          key={activeTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {displayNews.map((item, idx) => (
            <motion.div 
              key={item.id || idx}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.05 }}
              className="glass-card group overflow-hidden flex flex-col"
            >
              <div className="relative aspect-video overflow-hidden">
                <img 
                  src={item.image} 
                  alt={item.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded mb-2 inline-block ${tabConfigs[activeTab].color}`}>
                    {tabConfigs[activeTab].label}
                  </span>
                  <h3 className="text-2xl font-black leading-tight line-clamp-2">{item.title}</h3>
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-white/60 mb-6 flex-1 leading-relaxed line-clamp-3">{item.body}</p>
                <div className="flex items-center justify-between border-t border-white/5 pt-4">
                   <div className="flex items-center gap-2 text-white/30 text-xs">
                      <Clock className="w-3 h-3" />
                      <span>{new Date(news?.[activeTab]?.date).toLocaleDateString('fr-FR')}</span>
                   </div>
                   <a 
                     href={item.websiteUrl || 'https://www.fortnite.com/news'} 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="flex items-center gap-2 text-blue-400 font-bold text-sm hover:text-blue-300 transition-colors no-underline"
                   >
                      Détails <ExternalLink className="w-3 h-3" />
                   </a>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>
      
      {displayNews.length === 0 && (
        <div className="text-center py-20 px-8">
          <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
            <Newspaper className="w-8 h-8 text-white/20" />
          </div>
          <p className="text-white/40">Aucune actualité disponible pour ce mode en ce moment.</p>
        </div>
      )}
    </div>
  );
};

export default News;
